import { useCallback, useRef, useState } from "react";

const HISTORY_LIMIT = 50;

/**
 * 生成可持久化的画布快照（剥离函数属性与派生 normalized）
 * stripData: NodeCanvas 的 stripNonSerializable
 */
export function snapshotGraph(nodes, edges, stripData) {
  return {
    nodes: nodes.map((n) => {
      const data = stripData(n.data);
      delete data.normalized; // 派生数据，恢复时按 legacyType 重算
      return {
        id: n.id,
        type: n.type,
        position: { x: n.position.x, y: n.position.y },
        data,
      };
    }),
    edges: edges.map((e) => ({
      id: e.id,
      source: e.source,
      target: e.target,
      sourceHandle: e.sourceHandle ?? null,
      targetHandle: e.targetHandle ?? null,
      data: { connectionType: e.data?.connectionType || "reference" },
    })),
  };
}

/**
 * P2-2 画布撤销/重做
 * - 离散动作发生“前”调用 record(snapshot) 压栈
 * - undo/redo 返回应恢复的目标快照，由调用方负责应用并与后端同步
 */
export default function useCanvasHistory({ limit = HISTORY_LIMIT } = {}) {
  const pastRef = useRef([]);
  const futureRef = useRef([]);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const syncFlags = () => {
    setCanUndo(pastRef.current.length > 0);
    setCanRedo(futureRef.current.length > 0);
  };

  // 在变更“前”调用：把当前快照压入 past，并清空 redo 栈
  const record = useCallback(
    (snapshot) => {
      if (!snapshot) return;
      pastRef.current.push(snapshot);
      if (pastRef.current.length > limit) pastRef.current.shift();
      futureRef.current = [];
      syncFlags();
    },
    [limit]
  );

  // 撤销：currentSnapshot 为当前状态（进入 redo 栈），返回目标快照或 null
  const undo = useCallback((currentSnapshot) => {
    if (pastRef.current.length === 0) return null;
    const target = pastRef.current.pop();
    futureRef.current.push(currentSnapshot);
    syncFlags();
    return target;
  }, []);

  // 重做：currentSnapshot 为当前状态（回到 past 栈），返回目标快照或 null
  const redo = useCallback((currentSnapshot) => {
    if (futureRef.current.length === 0) return null;
    const target = futureRef.current.pop();
    pastRef.current.push(currentSnapshot);
    syncFlags();
    return target;
  }, []);

  return { record, undo, redo, canUndo, canRedo };
}
